import React, { useState, useEffect } from 'react';
import {
  Check,
  CircleCheck,
  ChevronDown,
  ChevronUp,
  Rocket,
  TrendingUp,
  Zap,
  Building2,
  Users,
  Eye,
  Plug,
  Clock,
  Unlock,
  Sparkles,
  Plus,
  Minus,
  Package,
  Star,
} from 'lucide-react';
import subscriptionApi, {
  SubscriptionTier,
  SubscriptionAddon,
  EstimateResult,
  TierPreview,
} from '../../services/subscriptionApi';
import FeatureComparisonAccordion from './FeatureComparisonAccordion';
import { getDisplayDiscount, PromoCampaign } from '../../utils/promoPricing';

interface SelectedAddon {
  addon_code: string;
  quantity: number;
}

interface TierConfiguratorProps {
  workspaceId: number;
  currentTierId?: number | null;
  currentBillingCycle?: 'monthly' | 'annual';
  currentAddons?: SelectedAddon[];
  currentPromoCode?: string | null;
  isLifetime?: boolean;
lifetimeStackedValues?: {
    named_users: number;
    public_view_users: number;
    api_endpoints?: number;
    custom_domains?: number;
    branding_policies?: number;
  };
  onConfigurationChange?: (config: {
    tier: SubscriptionTier;
    billingCycle: 'monthly' | 'annual';
    addons: SelectedAddon[];
    estimate: EstimateResult | null;
  }) => void;
  onConfirm?: (config: {
    tier: SubscriptionTier;
    billingCycle: 'monthly' | 'annual';
    addons: SelectedAddon[];
    estimate: EstimateResult;
  }) => void;
showConfirmButton?: boolean;
  confirmButtonText?: string;
  disabled?: boolean;
  parityDiscountPct?: number | null;
  parityCountry?: string | null;
  campaign?: PromoCampaign | null;
  api?: {
    getTiersForWorkspace: () => Promise<{ tiers: SubscriptionTier[] }>;
    getAddonsForTier: (tierId: number) => Promise<SubscriptionAddon[]>;
    previewTierLicense: (tierId: number) => Promise<TierPreview>;
    getEstimate: (options: any) => Promise<EstimateResult>;
  };
}

// Helper to get tier icon based on icon_name from API
const getTierIcon = (iconName: string | null): React.ElementType => {
  switch (iconName) {
    case 'rocket':
      return Rocket;
    case 'trending_up':
      return TrendingUp;
    case 'bolt':
      return Zap;
    case 'business':
      return Building2;
    default:
      return Rocket;
  }
};

// Helper to get gradient based on icon_name
const getTierGradient = (iconName: string | null): string => {
  switch (iconName) {
    case 'rocket':
      return 'linear-gradient(135deg, #4f46e5 0%, #818cf8 100%)';
    case 'trending_up':
      return 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)';
    case 'bolt':
      return 'linear-gradient(135deg, #d97706 0%, #fbbf24 100%)';
    case 'business':
      return 'linear-gradient(135deg, #0f172a 0%, #334155 100%)';
    default:
      return 'linear-gradient(135deg, #4f46e5 0%, #818cf8 100%)';
  }
};

// Calculate savings percentage dynamically
const calculateSavingsPercent = (monthly: number, annual: number): number => {
  if (monthly <= 0 || annual <= 0) return 0;
  const yearlyAtMonthly = monthly * 12;
  const savings = ((yearlyAtMonthly - annual) / yearlyAtMonthly) * 100;
  return Math.round(savings);
};

const TierConfigurator: React.FC<TierConfiguratorProps> = ({
  workspaceId,
  currentTierId,
  currentBillingCycle = 'monthly',
  currentAddons = [],
  parityDiscountPct = null,
  parityCountry = null,
  campaign = null,
  currentPromoCode = null,
  isLifetime = false,
  lifetimeStackedValues,
  onConfigurationChange,
  onConfirm,
  showConfirmButton = true,
  confirmButtonText = 'Continue',
  disabled = false,
  api,
}) => {
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>(currentBillingCycle);
  const [addons, setAddons] = useState<SubscriptionAddon[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<SelectedAddon[]>(currentAddons);
  const [estimate, setEstimate] = useState<EstimateResult | null>(null);

  const [loading, setLoading] = useState(true);
  const [addonsLoading, setAddonsLoading] = useState(false);
  const [tierPreviews, setTierPreviews] = useState<Record<number, TierPreview>>({});
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddons, setShowAddons] = useState(false);

  // Access code state
  const [accessCode, setAccessCode] = useState('');
  const [accessCodeValidated, setAccessCodeValidated] = useState(false);
  const [accessCodeError, setAccessCodeError] = useState<string | null>(null);
  const [unlockedTier, setUnlockedTier] = useState<SubscriptionTier | null>(null);
  const [showAccessCodeInput, setShowAccessCodeInput] = useState(false);
  const [accessCodeFocused, setAccessCodeFocused] = useState(false);

  // Hover states
  const [hoveredTier, setHoveredTier] = useState<number | null>(null);
  const [confirmHovered, setConfirmHovered] = useState(false);

  // Calculate average savings across tiers for the toggle badge
  const avgSavingsPercent = React.useMemo(() => {
    const tiersWithPrices = tiers.filter(
      (t) => !t.is_contact_sales && parseFloat(t.price_monthly) > 0 && parseFloat(t.price_annual) > 0
    );
    if (tiersWithPrices.length === 0) return 20;
    const totalSavings = tiersWithPrices.reduce(
      (sum, t) => sum + calculateSavingsPercent(parseFloat(t.price_monthly), parseFloat(t.price_annual)),
      0
    );
    return Math.round(totalSavings / tiersWithPrices.length);
  }, [tiers]);

  // Compute display tiers (include unlocked, hide enterprise if needed)
  const displayTiers = React.useMemo(() => {
    let result = tiers;

    if (unlockedTier) {
      const alreadyInList = tiers.some((t) => t.id === unlockedTier.id);
      if (!alreadyInList) {
        result = [unlockedTier, ...tiers];
      }
      result = result.filter((t) => t.tier_code !== 'enterprise');
    }

    return result;
  }, [tiers, unlockedTier]);

  // Load tiers on mount
  useEffect(() => {
    loadTiers();
  }, []);

  // Load addons when tier changes
  useEffect(() => {
    if (selectedTier) {
      loadAddons(selectedTier.id);
    }
  }, [selectedTier?.id]);

  // Load tier previews for lifetime users
  useEffect(() => {
    const loadPreviews = async () => {
      if (!isLifetime || tiers.length === 0) return;

      const previews: Record<number, TierPreview> = {};
      for (const tier of tiers) {
        try {
          const preview = api 
            ? await api.previewTierLicense(tier.id)
            : await subscriptionApi.previewTierLicense(workspaceId, tier.id);
          previews[tier.id] = preview;
        } catch (err) {
          console.error(`Failed to load preview for tier ${tier.id}:`, err);
        }
      }
      setTierPreviews(previews);
    };

    loadPreviews();
  }, [isLifetime, tiers, workspaceId, api]);

  // Fetch estimate when configuration changes
  useEffect(() => {
    const doFetch = async () => {
      if (!selectedTier || selectedTier.is_contact_sales) return;

      try {
        setEstimateLoading(true);
        const estimateOptions = {
          tier_id: selectedTier.id,
          billing_cycle: billingCycle,
          addons: selectedAddons.filter((a) => a.quantity > 0),
          promo_code: currentPromoCode || undefined,
        };
        const result = api 
          ? await api.getEstimate(estimateOptions)
          : await subscriptionApi.getEstimate(workspaceId, estimateOptions);
        setEstimate(result);
      } catch (err: any) {
        console.error('Failed to get estimate:', err);
        setEstimate(null);
      } finally {
        setEstimateLoading(false);
      }
    };
    doFetch();
  }, [workspaceId, selectedTier?.id, billingCycle, JSON.stringify(selectedAddons), api]);

  // Notify parent of configuration changes
  useEffect(() => {
    if (selectedTier && onConfigurationChange) {
      onConfigurationChange({
        tier: selectedTier,
        billingCycle,
        addons: selectedAddons,
        estimate,
      });
    }
  }, [selectedTier, billingCycle, selectedAddons, estimate]);

  const loadTiers = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = api 
        ? await api.getTiersForWorkspace()
        : await subscriptionApi.getTiersForWorkspace(workspaceId);
      let fetchedTiers = result.tiers.filter((t: SubscriptionTier) => t.is_active && !t.is_trial_tier);

      const hasNonPublicTier = fetchedTiers.some((t: any) => t.is_public === false);
      if (hasNonPublicTier) {
        fetchedTiers = fetchedTiers.filter((t: SubscriptionTier) => t.tier_code !== 'enterprise');
      }

      setTiers(fetchedTiers);

      const defaultTier =
        fetchedTiers.find((t) => t.id === currentTierId) ||
        fetchedTiers.find((t) => t.tier_code !== 'enterprise') ||
        fetchedTiers[0];
      if (defaultTier) {
        setSelectedTier(defaultTier);
      }
    } catch (err: any) {
      setError('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const loadAddons = async (tierId: number) => {
    try {
      setAddonsLoading(true);
      const fetchedAddons = api 
        ? await api.getAddonsForTier(tierId)
        : await subscriptionApi.getAddonsForTier(tierId);
      setAddons(fetchedAddons);
      if (selectedAddons.length === 0 && tierId === currentTierId && currentAddons.length > 0) {
        setSelectedAddons(currentAddons);
      } else if (tierId !== currentTierId && selectedAddons.length === 0) {
        setSelectedAddons([]);
      }
    } catch (err) {
      console.error('Failed to load addons:', err);
    } finally {
      setAddonsLoading(false);
    }
  };

  // Access code handlers
  const validateAccessCode = async (code: string) => {
    if (!code.trim()) {
      setAccessCodeValidated(false);
      setAccessCodeError(null);
      setUnlockedTier(null);
      return;
    }

    try {
      const response = await fetch('/api/tier-access/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ code: code.toUpperCase() }),
      });
      const data = await response.json();

      if (data.valid && data.accessCode) {
        setAccessCodeValidated(true);
        setAccessCodeError(null);
        setUnlockedTier(data.accessCode.tier);
        setSelectedTier(data.accessCode.tier);
      } else {
        setAccessCodeValidated(false);
        setAccessCodeError(data.error || 'Invalid access code');
        setUnlockedTier(null);
      }
    } catch (err) {
      setAccessCodeError('Failed to validate code');
      setAccessCodeValidated(false);
    }
  };

  const handleAccessCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setAccessCode(value);
    setAccessCodeValidated(false);
    setAccessCodeError(null);
  };

  const handleAccessCodeApply = () => {
    if (accessCode.trim()) {
      validateAccessCode(accessCode);
    }
  };

  const handleTierSelect = (tier: SubscriptionTier) => {
    if (disabled) return;
    setSelectedTier(tier);

    if (tier.base_branding_policies === -1 || (tier.base_branding_policies ?? 0) >= 9999) {
setSelectedAddons((prev) => prev.filter((a) => a.addon_code !== 'extra_branding_3'));
    }
  };

  const handleBillingCycleChange = (newCycle: 'monthly' | 'annual') => {
    if (!disabled) {
      setBillingCycle(newCycle);
    }
  };

  const handleAddonQuantityChange = (addonCode: string, delta: number) => {
    if (disabled) return;

    setSelectedAddons((prev) => {
      const existing = prev.find((a) => a.addon_code === addonCode);
      if (existing) {
        const newQty = Math.max(0, existing.quantity + delta);
        if (newQty === 0) {
          return prev.filter((a) => a.addon_code !== addonCode);
        }
        return prev.map((a) => (a.addon_code === addonCode ? { ...a, quantity: newQty } : a));
      } else if (delta > 0) {
        return [...prev, { addon_code: addonCode, quantity: delta }];
      }
      return prev;
    });
  };

  const getAddonQuantity = (addonCode: string): number => {
    return selectedAddons.find((a) => a.addon_code === addonCode)?.quantity || 0;
  };

  const disc = getDisplayDiscount({ billingCycle, parityPct: parityDiscountPct, parityCountry, campaign });
  const parityFactor = disc.factor;

  const formatPrice = (tier: SubscriptionTier): string => {
    if (tier.is_contact_sales) return 'Custom';
if ((tier as any).is_lifetime) return 'Lifetime';
    const price = (billingCycle === 'annual' ? parseFloat(tier.price_annual) / 12 : parseFloat(tier.price_monthly)) * parityFactor;
    if (isNaN(price)) return 'Lifetime';
    return `$${Math.round(price)}`;
  };
  const formatOriginalPrice = (tier: SubscriptionTier): string => {
    if (tier.is_contact_sales || (tier as any).is_lifetime) return '';
    const price = billingCycle === 'annual' ? parseFloat(tier.price_annual) / 12 : parseFloat(tier.price_monthly);
    if (isNaN(price)) return '';
    return `$${Math.round(price)}`;
  };

  const getAnnualSavings = (): number => {
    if (!selectedTier) return 0;
    const monthly = parseFloat(selectedTier.price_monthly);
    const annual = parseFloat(selectedTier.price_annual);
    return Math.round(monthly * 12 - annual);
  };

  const handleConfirm = () => {
    if (selectedTier && estimate && onConfirm) {
      onConfirm({
        tier: selectedTier,
        billingCycle,
        addons: selectedAddons,
        estimate,
      });
    }
  };

  // Derived
  const userAddons = addons.filter((a) => a.addon_type === 'user_allocation');
  const featureAddons = addons.filter((a) => a.addon_type === 'license_feature');
  const hasAddons = addons.length > 0;
  const totalSelectedAddons = selectedAddons.reduce((sum, a) => sum + a.quantity, 0);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '64px' }}>
        <div
          style={{
            width: '32px',
            height: '32px',
            border: '2px solid var(--v2-border)',
            borderTopColor: 'var(--v2-primary)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          backgroundColor: 'var(--v2-error-bg)',
          border: '1px solid #fecaca',
          borderRadius: 'var(--radius-md)',
          padding: '16px',
          color: '#991b1b',
          fontSize: '0.875rem',
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'var(--font-sans)' }}>
      {/* Access Code Section */}
      <div style={{ marginBottom: '24px' }}>
        {!showAccessCodeInput ? (
          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => setShowAccessCodeInput(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: 'none',
                border: 'none',
                color: 'var(--v2-primary)',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <Unlock size={14} strokeWidth={1.5} />
              Have an access code?
            </button>
          </div>
        ) : (
          <div
            style={{
              backgroundColor: 'var(--v2-surface-alt)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-md)',
              padding: '16px',
            }}
          >
            <label
              style={{
                display: 'block',
                fontSize: '0.6875rem',
                fontWeight: 500,
                color: 'var(--v2-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '8px',
              }}
            >
              Access Code
            </label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  value={accessCode}
                  onChange={handleAccessCodeChange}
                  onFocus={() => setAccessCodeFocused(true)}
                  onBlur={() => setAccessCodeFocused(false)}
                  placeholder="Enter code"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: 'var(--v2-surface)',
                    border: `1px solid ${accessCodeError ? 'var(--v2-error)' : accessCodeValidated ? 'var(--v2-success)' : accessCodeFocused ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.875rem',
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--v2-text)',
                    outline: 'none',
                    boxSizing: 'border-box',
                    boxShadow: accessCodeFocused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                  }}
                />
                {(accessCodeError || accessCodeValidated) && (
                  <p
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '0.8125rem',
                      color: accessCodeError ? 'var(--v2-error)' : 'var(--v2-success-dark)',
                      margin: '6px 0 0 0',
                    }}
                  >
                    {accessCodeValidated && <Check size={14} strokeWidth={2} />}
                    {accessCodeError || `Unlocked: ${unlockedTier?.tier_name}`}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleAccessCodeApply}
                disabled={!accessCode.trim() || accessCodeValidated}
                style={{
                  padding: '10px 16px',
                  backgroundColor: !accessCode.trim() || accessCodeValidated ? 'var(--v2-border)' : 'var(--v2-surface)',
                  color: !accessCode.trim() || accessCodeValidated ? 'var(--v2-text-muted)' : 'var(--v2-text)',
                  border: '1px solid var(--v2-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: !accessCode.trim() || accessCodeValidated ? 'not-allowed' : 'pointer',
                }}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Unlocked Tier Notice */}
      {unlockedTier && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            backgroundColor: 'var(--v2-success-bg)',
            border: '1px solid #86efac',
            borderRadius: 'var(--radius-md)',
            padding: '12px 16px',
            marginBottom: '24px',
          }}
        >
          <Sparkles size={16} strokeWidth={1.5} color="var(--v2-success-dark)" />
          <p style={{ fontSize: '0.875rem', color: 'var(--v2-success-dark)', margin: 0 }}>
            Special plan unlocked: <strong style={{ fontWeight: 600 }}>{unlockedTier.tier_name}</strong>
          </p>
        </div>
      )}

      {/* Billing Cycle Toggle */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
        <div
          style={{
            display: 'inline-flex',
            backgroundColor: 'var(--v2-surface-alt)',
            borderRadius: 'var(--radius-md)',
            padding: '4px',
            gap: '4px',
            border: '1px solid var(--v2-border)',
          }}
        >
          <button
            onClick={() => handleBillingCycleChange('monthly')}
            disabled={disabled}
            style={{
              padding: '10px 20px',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: billingCycle === 'monthly' ? '#ffffff' : 'var(--v2-text-tertiary)',
              backgroundColor: billingCycle === 'monthly' ? 'var(--v2-primary)' : 'transparent',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            Monthly
          </button>
          <button
            onClick={() => handleBillingCycleChange('annual')}
            disabled={disabled}
            style={{
              padding: '10px 20px',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: billingCycle === 'annual' ? '#ffffff' : 'var(--v2-text-tertiary)',
              backgroundColor: billingCycle === 'annual' ? 'var(--v2-primary)' : 'transparent',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            Annual
            <span
              style={{
                backgroundColor: billingCycle === 'annual' ? 'rgba(255,255,255,0.2)' : 'var(--v2-success-bg)',
                color: billingCycle === 'annual' ? '#ffffff' : 'var(--v2-success-dark)',
                padding: '2px 8px',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.6875rem',
                fontWeight: 700,
              }}
            >
              SAVE {avgSavingsPercent}%
            </span>
          </button>
        </div>
      </div>

      {/* Tier Cards Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(displayTiers.length, 4)}, minmax(220px, 1fr))`,
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {displayTiers.map((tier) => {
          const isSelected = selectedTier?.id === tier.id;
          const isCurrent = currentTierId === tier.id;
          const isUnlocked = unlockedTier?.id === tier.id;
          const isHovered = hoveredTier === tier.id;
          const TierIcon = getTierIcon(tier.icon_name);
          const tierGradient = getTierGradient(tier.icon_name);
          const tierSavings = calculateSavingsPercent(parseFloat(tier.price_monthly), parseFloat(tier.price_annual));

          return (
            <div
              key={tier.id}
              onClick={() => !tier.is_contact_sales && handleTierSelect(tier)}
              onMouseEnter={() => setHoveredTier(tier.id)}
              onMouseLeave={() => setHoveredTier(null)}
              style={{
                position: 'relative',
                backgroundColor: 'var(--v2-surface)',
                borderRadius: 'var(--radius-lg)',
                border: `2px solid ${isSelected ? 'var(--v2-primary)' : isHovered ? 'var(--v2-text-muted)' : 'var(--v2-border)'}`,
                boxShadow: isSelected ? '0 4px 12px rgba(79, 70, 229, 0.15)' : 'var(--shadow-sm)',
                cursor: tier.is_contact_sales || disabled ? 'default' : 'pointer',
                transition: 'all 0.2s ease',
                transform: isSelected ? 'translateY(-2px)' : 'none',
                overflow: 'visible',
              }}
            >
              {/* Unlocked Badge */}
              {isUnlocked && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
                    color: 'white',
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    padding: '4px 12px',
                    borderRadius: 'var(--radius-full)',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Check size={10} strokeWidth={3} />
                  Unlocked
                </div>
              )}

              {/* Popular Badge */}
              {tier.is_popular && !isUnlocked && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                    color: 'white',
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    padding: '4px 12px',
                    borderRadius: 'var(--radius-full)',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Most Popular
                </div>
              )}

              <div style={{ padding: '20px', paddingTop: tier.is_popular || isUnlocked ? '28px' : '20px' }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: 'var(--radius-md)',
                      background: tierGradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <TierIcon size={18} strokeWidth={2} color="#ffffff" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--v2-text)' }}>{tier.tier_name}</div>
                    {isCurrent && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                          fontSize: '0.625rem',
                          fontWeight: 600,
                          color: 'var(--v2-primary)',
                          backgroundColor: 'var(--v2-primary-light)',
                          padding: '2px 6px',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        <Star size={8} strokeWidth={2} />
                        CURRENT
                      </span>
                    )}
                  </div>
                  {isSelected && !tier.is_contact_sales && <CircleCheck size={22} strokeWidth={2} color="var(--v2-primary)" />}
                </div>

                {/* Price */}
                <div style={{ marginBottom: '16px' }}>
{parityFactor < 1 && !tier.is_contact_sales && !(tier as any).is_lifetime && (
                    <span
                      style={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: 'var(--v2-text-tertiary)',
                        textDecoration: 'line-through',
                        marginRight: '8px',
                        verticalAlign: 'middle',
                      }}
                    >
                      {formatOriginalPrice(tier)}
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: '2rem',
                      fontWeight: 700,
                      color: 'var(--v2-text)',
                      letterSpacing: '-0.02em',
                      verticalAlign: 'middle',
                    }}
                  >
                    {formatPrice(tier)}
                  </span>
                  {!tier.is_contact_sales && (
                    <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', marginLeft: '4px' }}>/mo</span>
                  )}
                  {parityFactor < 1 && !tier.is_contact_sales && !(tier as any).is_lifetime && (
                    <div style={{ marginTop: '6px' }}>
                      <span style={{ display: 'inline-block', backgroundColor: disc.kind === 'campaign' ? '#FF5847' : '#0e7490', color: '#ffffff', padding: '3px 8px', borderRadius: 'var(--radius-sm)', fontSize: '0.6875rem', fontWeight: 600 }}>
                        {disc.badge}
                      </span>
                    </div>
                  )}
                  {disc.hint && !tier.is_contact_sales && !(tier as any).is_lifetime && (
                    <div style={{ fontSize: '0.75rem', color: '#FF5847', fontWeight: 600, marginTop: '4px' }}>
                      {disc.hint}
                    </div>
                  )}
                  {billingCycle === 'annual' && !tier.is_contact_sales && tierSavings > 0 && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--v2-success)', fontWeight: 500, marginTop: '4px' }}>
                      ${Math.round(parseFloat(tier.price_annual) * parityFactor)}{disc.kind === 'campaign' ? ' first year' : '/year'} (save {disc.kind === 'campaign' ? Math.round((1 - (parseFloat(tier.price_annual) * parityFactor) / (parseFloat(tier.price_monthly) * 12)) * 100) : tierSavings}%)
                    </div>
                  )}
                </div>

                {/* Key Metrics */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: 'var(--v2-surface-alt)',
                    borderRadius: 'var(--radius-md)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Users size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
                      <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>Monthly Active Users (MAU)</span>
                    </div>
<span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-text)' }}>
                      {isLifetime && tierPreviews[tier.id] && !tier.tier_code.startsWith('lifetime_')
                        ? tierPreviews[tier.id].named_users
                        : tier.tier_code.startsWith('lifetime_') && lifetimeStackedValues
                        ? lifetimeStackedValues.named_users
                        : tier.base_named_users ?? 'Custom'}
                      {isLifetime &&
                        !tier.tier_code.startsWith('lifetime_') &&
                        tierPreviews[tier.id]?.includes_lifetime_bonus &&
                        (tierPreviews[tier.id]?.lifetime_bonus?.named_users ?? 0) > 0 && (
                          <span style={{ color: 'var(--v2-success)', marginLeft: '4px', fontSize: '0.75rem' }}>
                            +{tierPreviews[tier.id]?.lifetime_bonus?.named_users}
                          </span>
                        )}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Eye size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
                      <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>Public View</span>
                    </div>
<span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-text)' }}>
                      {isLifetime && tierPreviews[tier.id] && !tier.tier_code.startsWith('lifetime_')
                        ? tierPreviews[tier.id].public_view_users
                        : tier.tier_code.startsWith('lifetime_') && lifetimeStackedValues
                        ? lifetimeStackedValues.public_view_users
                        : tier.base_public_view_users ?? 'Custom'}
                      {isLifetime &&
                        !tier.tier_code.startsWith('lifetime_') &&
                        tierPreviews[tier.id]?.includes_lifetime_bonus &&
                        (tierPreviews[tier.id]?.lifetime_bonus?.public_view_users ?? 0) > 0 && (
                          <span style={{ color: 'var(--v2-success)', marginLeft: '4px', fontSize: '0.75rem' }}>
                            +{tierPreviews[tier.id]?.lifetime_bonus?.public_view_users}
                          </span>
                        )}
                    </span>
                  </div>
                </div>

                {/* Action Button */}
                {tier.is_contact_sales ? (
<a                  
                    href="mailto:sales@dashboardfox.com"
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      padding: '10px 16px',
                      backgroundColor: 'var(--v2-surface-alt)',
                      color: 'var(--v2-text)',
                      border: '1px solid var(--v2-border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      textDecoration: 'none',
                      transition: 'all 0.15s',
                    }}
                  >
                    Contact Sales
                  </a>
                ) : (
                  <button
                    disabled={disabled}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '10px 16px',
                      backgroundColor: isSelected ? 'var(--v2-primary)' : 'var(--v2-surface)',
                      color: isSelected ? '#ffffff' : 'var(--v2-text)',
                      border: isSelected ? 'none' : '1px solid var(--v2-border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s',
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >
                    {isSelected && <Check size={16} strokeWidth={2.5} />}
                    {isSelected ? 'Selected' : 'Select'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Feature Comparison Accordion */}
<FeatureComparisonAccordion
        tiers={displayTiers}
        selectedTierId={selectedTier?.id}
        isLifetime={isLifetime}
        tierPreviews={tierPreviews}
        lifetimeStackedValues={lifetimeStackedValues}
      />

      {/* Addons Section */}
      {selectedTier && !selectedTier.is_contact_sales && hasAddons && (
        <div
          style={{
            backgroundColor: 'var(--v2-surface)',
            border: '1px solid var(--v2-border)',
            borderRadius: 'var(--radius-lg)',
            marginTop: '24px',
            overflow: 'hidden',
          }}
        >
          <button
            onClick={() => setShowAddons(!showAddons)}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Package size={18} strokeWidth={1.5} color="var(--v2-text-secondary)" />
              <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--v2-text)' }}>Add-ons</span>
              {totalSelectedAddons > 0 && (
                <span
                  style={{
                    backgroundColor: 'var(--v2-primary)',
                    color: 'white',
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-full)',
                  }}
                >
                  {totalSelectedAddons} selected
                </span>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: showAddons ? 'var(--v2-primary-light)' : 'transparent',
                color: showAddons ? 'var(--v2-primary)' : 'var(--v2-text-muted)',
                transition: 'all 0.15s ease',
              }}
            >
              {showAddons ? <ChevronUp size={18} strokeWidth={2} /> : <ChevronDown size={18} strokeWidth={2} />}
            </div>
          </button>

          {showAddons && (
            <div style={{ borderTop: '1px solid var(--v2-border)', padding: '20px' }}>
              {addonsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      border: '2px solid var(--v2-border)',
                      borderTopColor: 'var(--v2-primary)',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                    }}
                  />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {/* User Addons */}
                  {userAddons.length > 0 && (
                    <div>
                      <p
                        style={{
                          fontSize: '0.6875rem',
                          fontWeight: 500,
                          color: 'var(--v2-text-muted)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginBottom: '12px',
                        }}
                      >
                        Additional Capacity
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {userAddons.map((addon) => {
                          const price =
                            billingCycle === 'annual'
                              ? parseFloat(addon.pricing?.[0]?.price_annual || '0') / 12
                              : parseFloat(addon.pricing?.[0]?.price_monthly || '0');
                          const qty = getAddonQuantity(addon.addon_code);

                          return (
                            <div
                              key={addon.id}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '14px 16px',
                                backgroundColor: qty > 0 ? 'var(--v2-primary-light)' : 'var(--v2-surface-alt)',
                                border: `1px solid ${qty > 0 ? '#c7d2fe' : 'var(--v2-border)'}`,
                                borderRadius: 'var(--radius-md)',
                                transition: 'all 0.15s',
                              }}
                            >
                              <div>
                                <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--v2-text)' }}>
                                  {addon.addon_name}
                                </div>
                                <div style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>
                                  ${Math.round(price)}/month each
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddonQuantityChange(addon.addon_code, -1);
                                  }}
                                  disabled={qty === 0 || disabled}
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'var(--v2-surface)',
                                    border: '1px solid var(--v2-border)',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: qty === 0 || disabled ? 'not-allowed' : 'pointer',
                                    opacity: qty === 0 ? 0.5 : 1,
                                    color: 'var(--v2-text-tertiary)',
                                  }}
                                >
                                  <Minus size={16} strokeWidth={2} />
                                </button>
                                <span
                                  style={{
                                    minWidth: '32px',
                                    textAlign: 'center',
                                    fontSize: '0.9375rem',
                                    fontWeight: 600,
                                    color: 'var(--v2-text)',
                                  }}
                                >
                                  {qty}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddonQuantityChange(addon.addon_code, 1);
                                  }}
                                  disabled={disabled}
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'var(--v2-surface)',
                                    border: '1px solid var(--v2-border)',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    color: 'var(--v2-text-tertiary)',
                                  }}
                                >
                                  <Plus size={16} strokeWidth={2} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

{/* Feature Addons */}
                  {featureAddons.length > 0 && (
                    <div>
                      <p
                        style={{
                          fontSize: '0.6875rem',
                          fontWeight: 500,
                          color: 'var(--v2-text-muted)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginBottom: '12px',
                        }}
                      >
                        Features
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {featureAddons.map((addon) => {
                          const price =
                            billingCycle === 'annual'
                              ? parseFloat(addon.pricing?.[0]?.price_annual || '0') / 12
                              : parseFloat(addon.pricing?.[0]?.price_monthly || '0');
                          const qty = getAddonQuantity(addon.addon_code);
                          const isQuantity = addon.license_config_type === 'quantity';
                          const isEnabled = qty > 0;

                          return (
                            <div
                              key={addon.id}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '14px 16px',
                                backgroundColor: isEnabled ? 'var(--v2-primary-light)' : 'var(--v2-surface-alt)',
                                border: `1px solid ${isEnabled ? '#c7d2fe' : 'var(--v2-border)'}`,
                                borderRadius: 'var(--radius-md)',
                                transition: 'all 0.15s',
                              }}
                            >
                              <div>
                                <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--v2-text)' }}>
                                  {addon.addon_name}
                                </div>
                                <div style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>
                                  ${Math.round(price)}/month{isQuantity ? ' each' : ''}
                                </div>
                              </div>
                              {isQuantity ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddonQuantityChange(addon.addon_code, -1);
                                    }}
                                    disabled={qty === 0 || disabled}
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: 'var(--v2-surface)',
                                      border: '1px solid var(--v2-border)',
                                      borderRadius: 'var(--radius-sm)',
                                      cursor: qty === 0 || disabled ? 'not-allowed' : 'pointer',
                                      opacity: qty === 0 ? 0.5 : 1,
                                      color: 'var(--v2-text-tertiary)',
                                    }}
                                  >
                                    <Minus size={16} strokeWidth={2} />
                                  </button>
                                  <span
                                    style={{
                                      minWidth: '32px',
                                      textAlign: 'center',
                                      fontSize: '0.9375rem',
                                      fontWeight: 600,
                                      color: 'var(--v2-text)',
                                    }}
                                  >
                                    {qty}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddonQuantityChange(addon.addon_code, 1);
                                    }}
                                    disabled={disabled}
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: 'var(--v2-surface)',
                                      border: '1px solid var(--v2-border)',
                                      borderRadius: 'var(--radius-sm)',
                                      cursor: disabled ? 'not-allowed' : 'pointer',
                                      color: 'var(--v2-text-tertiary)',
                                    }}
                                  >
                                    <Plus size={16} strokeWidth={2} />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddonQuantityChange(addon.addon_code, isEnabled ? -1 : 1);
                                  }}
                                  disabled={disabled}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '8px 16px',
                                    backgroundColor: isEnabled ? 'var(--v2-primary)' : 'var(--v2-surface)',
                                    color: isEnabled ? '#ffffff' : 'var(--v2-text)',
                                    border: isEnabled ? 'none' : '1px solid var(--v2-border)',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.15s',
                                  }}
                                >
                                  {isEnabled && <Check size={14} strokeWidth={2.5} />}
                                  {isEnabled ? 'Added' : 'Add'}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Order Summary */}
      {selectedTier && !selectedTier.is_contact_sales && (
        <div
          style={{
            backgroundColor: 'var(--v2-surface-alt)',
            border: '1px solid var(--v2-border)',
            borderRadius: 'var(--radius-lg)',
            padding: '24px',
            marginTop: '24px',
          }}
        >
          <h3
            style={{
              fontSize: '0.9375rem',
              fontWeight: 600,
              color: 'var(--v2-text)',
              margin: '0 0 16px 0',
            }}
          >
            Order Summary
          </h3>

          {estimateLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  border: '2px solid var(--v2-border)',
                  borderTopColor: 'var(--v2-primary)',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            </div>
          ) : estimate ? (
            <div>
              {/* Line Items */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                {(estimate.recurring_line_items || estimate.line_items).map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>
                      {item.description}
                      {item.quantity > 1 && ` (×${item.quantity})`}
                    </span>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--v2-text)' }}>
                      ${item.amount.toFixed(2)}
                    </span>
                  </div>
                ))}

                {estimate.discounts.map((discount, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--v2-success)' }}>
                      {discount.description}
                    </span>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--v2-success)' }}>
                      -${Math.abs(discount.amount).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div style={{ height: '1px', backgroundColor: 'var(--v2-border)', margin: '16px 0' }} />

              {/* Total */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--v2-text)' }}>
                  {billingCycle === 'annual' ? 'Total (billed annually)' : 'Total per month'}
                </span>
                <span style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--v2-primary)' }}>
                  ${(estimate.recurring_total ?? estimate.total).toFixed(2)}
                </span>
              </div>

              {billingCycle === 'annual' && getAnnualSavings() > 0 && (
                <div style={{ textAlign: 'right' }}>
                  <span
                    style={{
                      backgroundColor: 'var(--v2-success-bg)',
                      color: 'var(--v2-success-dark)',
                      padding: '4px 10px',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    You save ${getAnnualSavings()} per year
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
              Select a plan to see pricing
            </p>
          )}
        </div>
      )}

      {/* Confirm Button */}
      {showConfirmButton && selectedTier && !selectedTier.is_contact_sales && (
        <div style={{ marginTop: '24px' }}>
          <button
            onClick={handleConfirm}
            onMouseEnter={() => setConfirmHovered(true)}
            onMouseLeave={() => setConfirmHovered(false)}
            disabled={disabled || !estimate || estimateLoading}
            style={{
              width: '100%',
              padding: '16px 24px',
              backgroundColor:
                disabled || !estimate || estimateLoading
                  ? 'var(--v2-border)'
                  : confirmHovered
                    ? 'var(--v2-primary-hover)'
                    : 'var(--v2-primary)',
              color: disabled || !estimate || estimateLoading ? 'var(--v2-text-muted)' : '#ffffff',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: disabled || !estimate || estimateLoading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.15s',
            }}
          >
            {estimateLoading ? 'Calculating...' : confirmButtonText}
          </button>
        </div>
      )}
    </div>
  );
};

export default TierConfigurator;
